import type { MaybeRefOrGetter, Ref } from "vue";
import { useDropZone, useFileDialog } from "@vueuse/core";
import { computed, ref, toValue } from "vue";

export interface UseFileUploadOptions {
  /**
   * External files ref to use instead of creating internal state.
   * Useful for integrating with v-model or external state management.
   */
  files?: Ref<File[]>;

  /**
   * Ref to the drop zone element for drag-and-drop support
   * If not provided, drag-and-drop is disabled
   */
  dropZoneRef?: MaybeRefOrGetter<HTMLElement | null | undefined>;

  /**
   * Accepted file types (e.g., ['application/pdf', 'image/jpeg', 'image/*'])
   * Supports MIME types and file extensions.
   * Reactive — updates are applied on each file dialog open.
   */
  accept?: MaybeRefOrGetter<string[]>;

  /**
   * Maximum number of files allowed
   * When > 1, multiple file selection is enabled.
   * Reactive — updates are applied on each file dialog open.
   * @default 1
   */
  maxFiles?: MaybeRefOrGetter<number>;

  /**
   * Disable file selection
   * @default false
   */
  disabled?: MaybeRefOrGetter<boolean>;
}

export interface RejectedFile {
  file: File;
  reason: "type" | "limit";
  message: string;
}

export interface UseFileUploadReturn {
  /** Current list of valid files */
  files: Ref<File[]>;

  /** Whether a file is currently being dragged over the drop zone */
  isOverDropZone: Ref<boolean>;

  /** Files rejected during the last selection (reset on each selection) */
  rejectedFiles: Ref<RejectedFile[]>;

  /** Whether the maximum number of files has been reached */
  isMaxFilesReached: Readonly<Ref<boolean>>;

  /** Open the file selection dialog */
  openFileDialog: () => void;

  /** Add files programmatically (validates and filters) */
  addFiles: (newFiles: File[]) => void;

  /** Remove a file by index */
  removeFile: (index: number) => void;

  /** Clear all files */
  clearFiles: () => void;
}

export function useFileUpload(options: UseFileUploadOptions = {}): UseFileUploadReturn {
  const files = options.files ?? ref<File[]>([]);
  const rejectedFiles = ref<RejectedFile[]>([]);

  const dropZoneElement = computed(() => toValue(options.dropZoneRef) ?? null);

  const accept = computed(() => toValue(options.accept) ?? []);
  const maxFiles = computed(() => toValue(options.maxFiles) || 1);
  const isMaxFilesReached = computed(() => files.value.length >= maxFiles.value);

  const { open: openDialog, reset: resetDialog, onChange } = useFileDialog();

  // Process and add files with validation
  function processFiles(newFiles: File[]) {
    if (toValue(options.disabled) || newFiles.length === 0) {
      return;
    }

    const rejected: RejectedFile[] = [];
    const accepted: File[] = [];

    const currentAccept = accept.value;
    const currentMaxFiles = maxFiles.value;
    const acceptedLabel = formatAcceptedTypes(currentAccept);
    const limitMessage = `Nombre maximum de fichiers atteint (${currentMaxFiles})`;

    // Validate file types
    for (const file of newFiles) {
      if (isFileTypeAccepted(file, currentAccept)) {
        accepted.push(file);
      }
      else {
        const receivedLabel = formatFileType(file);
        const typeMessage = currentAccept.length > 0
          ? `Format ${receivedLabel} non supporté. Formats acceptés : ${acceptedLabel}`
          : `Format ${receivedLabel} non supporté`;
        rejected.push({ file, reason: "type", message: typeMessage });
      }
    }

    // Add files up to maxFiles limit.
    // When no slots remain, new files are rejected — consumers should use
    // `isMaxFilesReached` to prevent interaction before this point.
    const currentCount = files.value.length;
    const remainingSlots = currentMaxFiles - currentCount;

    if (remainingSlots <= 0) {
      // No slots available, reject all
      for (const file of accepted) {
        rejected.push({ file, reason: "limit", message: limitMessage });
      }
    }
    else {
      const filesToAdd = accepted.slice(0, remainingSlots);
      const filesToReject = accepted.slice(remainingSlots);

      // In single mode (maxFiles=1), replace; in multiple mode, append
      if (currentMaxFiles === 1) {
        files.value = filesToAdd;
      }
      else {
        files.value = [...files.value, ...filesToAdd];
      }

      for (const file of filesToReject) {
        rejected.push({ file, reason: "limit", message: limitMessage });
      }
    }

    rejectedFiles.value = rejected;
  }

  // Handle file dialog selection
  onChange((fileList) => {
    try {
      if (fileList) {
        processFiles(Array.from(fileList));
      }
    }
    catch (error) {
      console.warn("[AwFileUpload] Error processing selected files:", error);
    }
  });

  // Drop zone from VueUse (only active if dropZoneRef is provided)
  const { isOverDropZone } = useDropZone(dropZoneElement, {
    onDrop: (droppedFiles) => {
      try {
        if (droppedFiles) {
          processFiles(droppedFiles);
        }
      }
      catch (error) {
        console.warn("[AwFileUpload] Error processing dropped files:", error);
      }
    },
  });

  function openFileDialog() {
    if (!toValue(options.disabled)) {
      resetDialog();
      openDialog({ accept: accept.value.join(","), multiple: maxFiles.value > 1 });
    }
  }

  function addFiles(newFiles: File[]) {
    processFiles(newFiles);
  }

  function removeFile(index: number) {
    files.value = files.value.filter((_, i) => i !== index);
    resetDialog();
  }

  function clearFiles() {
    files.value = [];
    rejectedFiles.value = [];
    resetDialog();
  }

  return {
    files,
    isOverDropZone,
    rejectedFiles,
    isMaxFilesReached,
    openFileDialog,
    addFiles,
    removeFile,
    clearFiles,
  };
}

export const MIME_TYPE_LABELS: Record<string, string> = {
  "image/*": "PNG, JPG",
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/jpg": "JPG",
  "image/gif": "GIF",
  "image/webp": "WEBP",
  "image/svg+xml": "SVG",
  "application/pdf": "PDF",
  "text/plain": "TXT",
  "text/csv": "CSV",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.ms-excel": "XLS",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
};

const SHORTHAND_TO_MIME: Record<string, string[]> = {
  png: ["image/png"],
  jpg: ["image/jpeg", "image/jpg"],
  jpeg: ["image/jpeg", "image/jpg"],
  gif: ["image/gif"],
  webp: ["image/webp"],
  svg: ["image/svg+xml"],
  pdf: ["application/pdf"],
  txt: ["text/plain"],
  csv: ["text/csv"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  xls: ["application/vnd.ms-excel"],
  doc: ["application/msword"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
};

function formatFileType(file: File): string {
  if (file.type && MIME_TYPE_LABELS[file.type.toLowerCase()]) {
    return MIME_TYPE_LABELS[file.type.toLowerCase()];
  }
  const ext = file.name.split(".").pop();
  if (ext) {
    return ext.toUpperCase();
  }
  return file.type || "inconnu";
}

function formatAcceptedTypes(acceptedTypes: string[]): string {
  const labels = acceptedTypes.map((type) => {
    const lower = type.toLowerCase();
    if (MIME_TYPE_LABELS[lower]) {
      return MIME_TYPE_LABELS[lower];
    }
    if (lower.startsWith(".")) {
      return lower.slice(1).toUpperCase();
    }
    return type;
  });
  return labels.join(", ");
}

/**
 * Checks if a file matches the accepted types.
 * Supports MIME types (e.g., 'application/pdf', 'image/*') and file extensions (e.g., '.pdf', 'pdf')
 */
function isFileTypeAccepted(file: File, acceptedTypes: string[]): boolean {
  if (acceptedTypes.length === 0) {
    return true;
  }

  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return acceptedTypes.some((acceptValue) => {
    const lowerAccept = acceptValue.toLowerCase();

    // Check file extension (e.g., '.pdf' or 'pdf')
    const extension = lowerAccept.startsWith(".") ? lowerAccept : `.${lowerAccept}`;
    if (fileName.endsWith(extension)) {
      return true;
    }

    // Check exact MIME type match
    if (lowerAccept.includes("/") && fileType === lowerAccept) {
      return true;
    }

    // Check wildcard MIME type (e.g., 'image/*')
    if (lowerAccept.endsWith("/*")) {
      const baseType = lowerAccept.slice(0, -2);
      if (fileType.startsWith(`${baseType}/`)) {
        return true;
      }
    }

    // Shorthand extension to MIME type matching (e.g., "png" → "image/png")
    const mimeTypes = SHORTHAND_TO_MIME[lowerAccept];
    if (mimeTypes && mimeTypes.includes(fileType)) {
      return true;
    }

    return false;
  });
}
